import { Controller } from '@hotwired/stimulus'
import SFetch from '@app/utils/sfetch'
import { SfLog } from '@app/adapters/logger'

export default class extends Controller {
  static values = {
    url: String,
    interval: { type: Number, default: 5000 },
  }

  declare readonly urlValue: string
  declare readonly intervalValue: number

  private timer: ReturnType<typeof setTimeout> | null = null
  private disconnected = false

  connect(): void {
    this.disconnected = false
    this.scheduleNextPoll()
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  disconnect(): void {
    this.disconnected = true
    this.stopPolling()
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    )
  }

  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      this.stopPolling()
    } else {
      this.scheduleNextPoll()
    }
  }

  private scheduleNextPoll(): void {
    if (this.timer) return
    if (this.pendingIds.length === 0) return
    this.timer = setTimeout(() => {
      this.timer = null
      void this.poll()
    }, this.intervalValue)
  }

  private stopPolling(): void {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  private get pendingIds(): string[] {
    return [...this.element.querySelectorAll('[data-queue-id]')].map(
      (el) => (el as HTMLElement).dataset.queueId as string
    )
  }

  private async poll(): Promise<void> {
    const ids = this.pendingIds
    if (ids.length === 0) return

    try {
      const response = await SFetch.raw(
        `${this.urlValue}?ids=${ids.join(',')}`,
        {
          headers: { Accept: 'text/vnd.turbo-stream.html' },
        }
      )

      if (this.disconnected) return

      const html = await response.text()
      this.renderStreamMessage(html)
      this.scheduleNextPoll()
    } catch (error) {
      if (this.disconnected) return

      SfLog.warn('Error polling queue batch progress', { extra: { error } })
      this.scheduleNextPoll()
    }
  }

  // Append turbo-stream elements to the DOM so Turbo's MutationObserver
  // picks them up, processes the replace action, and removes them.
  // We avoid importing from @hotwired/turbo directly because the package
  // ships no TypeScript declarations with the moduleResolution used here.
  // This DOM-insertion approach is equivalent to renderStreamMessage() and
  // is the same mechanism ActionCable turbo_stream_from uses at runtime.
  private renderStreamMessage(html: string): void {
    // Dismiss any visible tooltips before swapping rows — otherwise the tooltip
    // bubble stays orphaned in the DOM after its anchor element is replaced.
    document.querySelectorAll('.sf-tooltip').forEach((el) => el.remove())

    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    doc.querySelectorAll('turbo-stream').forEach((el) => {
      document.body.appendChild(el)
    })
  }
}
